/**
 * Shared helpers for presenting subscription courses to a student.
 *
 * A subscription course is an ordinary Product whose type subtype code is
 * SUBSCRIPTION_FEE: instead of a one-off fee it carries a recurring price
 * (price.priceAmt) charged every price.priceDurationType. Once the first order
 * is paid the backend puts a MemberSubscription on the User, which is what the
 * profile section reads back.
 */

export const SUBSCRIPTION_PRODUCT_SUBTYPE = "SUBSCRIPTION_FEE";

/** Label for the profile card and page heading when a company sells subscription courses. */
export const PURCHASE_AND_SUBSCRIPTION_LABEL = "Purchase And Subscription Details";

/** Label used when the company only sells one-off course purchases. */
export const PURCHASE_ONLY_LABEL = "Purchase Details";

/**
 * Reads the product subtype code, tolerating the several shapes a product
 * arrives in (bare product, order line item wrapper, flattened summary row).
 */
export const getProductSubtypeCode = (product = {}) => String(
  product?.type?.subtypeCode ||
  product?.product?.type?.subtypeCode ||
  product?.subtypeCode ||
  ""
).toUpperCase();

export const isSubscriptionProduct = (product = {}) => (
  getProductSubtypeCode(product) === SUBSCRIPTION_PRODUCT_SUBTYPE
);

/**
 * Turns a DurationType / PaymentFrequencyType value into the billing cycle
 * wording shown next to a recurring price. Unknown values render as "" so the
 * caller can drop the "/ cycle" suffix entirely.
 */
export const getBillingCycleLabel = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "MONTH" || normalized === "MONTHLY") return "Monthly";
  if (normalized === "YEAR" || normalized === "ANNUAL" || normalized === "ANNUALLY") return "Annual";
  if (normalized === "WEEK" || normalized === "WEEKLY") return "Weekly";
  if (normalized === "DAY" || normalized === "DAILY") return "Daily";
  return "";
};

/** True when the company has subscription courses switched on in the admin console. */
export const isSubscriptionEnabledCompany = (company) => Boolean(company?.supportSubscriptionProducts);

/**
 * Heading for the purchase section of the student profile. Companies selling
 * subscription courses get the combined wording, everyone else keeps the
 * original label.
 */
export const getPurchaseSectionLabel = (company) => (
  isSubscriptionEnabledCompany(company) ? PURCHASE_AND_SUBSCRIPTION_LABEL : PURCHASE_ONLY_LABEL
);

const SUBSCRIPTION_STATUS_LABELS = {
  ACTIVE: "Active",
  PAST_DUE: "Past Due",
  CANCELLED: "Cancelled",
  SUSPENDED: "Suspended"
};

/** Maps SubscriptionStatusType to its display wording. */
export const getSubscriptionStatusLabel = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  return SUBSCRIPTION_STATUS_LABELS[normalized] || (normalized ? "Unknown" : "");
};

/** Tailwind classes for the subscription status pill. */
export const getSubscriptionStatusTone = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "ACTIVE") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (normalized === "PAST_DUE") return "bg-amber-50 text-amber-700 border-amber-200";
  if (normalized === "SUSPENDED") return "bg-orange-50 text-orange-700 border-orange-200";
  if (normalized === "CANCELLED") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
};

const PAID_ORDER_STATUSES = ["PAID", "COMPLETED"];
const PENDING_ORDER_STATUSES = ["CREATED", "PAYMENT_PENDING", "PAYMENT_PROCESSING"];

/**
 * Resolves the payment status of a single subscription bill.
 *
 * The invoice status is authoritative; a bill whose invoice has not been loaded
 * yet falls back to the status of the order the bill was raised against, and a
 * bill with neither is reported as Pending because the billing run only creates
 * a bill when payment is due.
 */
export const getBillPaymentStatus = (invoice, order) => {
  const invoiceStatus = String(invoice?.status || "").trim().toUpperCase();
  if (invoiceStatus === "PAID") return "Paid";
  if (invoiceStatus === "OVERDUE") return "Overdue";
  if (invoiceStatus === "REFUNDED") return "Refunded";
  if (invoiceStatus === "CANCELLED") return "Cancelled";
  if (invoiceStatus === "PENDING" || invoiceStatus === "CREATED") return "Pending";

  const orderStatus = String(order?.currentStatus || order?.status || "").trim().toUpperCase();
  if (PAID_ORDER_STATUSES.includes(orderStatus)) return "Paid";
  if (PENDING_ORDER_STATUSES.includes(orderStatus)) return "Pending";
  if (orderStatus === "CANCELLED") return "Cancelled";
  if (orderStatus === "REFUNDED" || orderStatus === "PARTIALLY_REFUNDED") return "Refunded";

  return "Pending";
};

/** Tailwind classes for the payment status pill. */
export const getPaymentStatusTone = (paymentStatus) => {
  switch (paymentStatus) {
    case "Paid":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "Overdue":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "Refunded":
      return "bg-sky-50 text-sky-700 border-sky-200";
    default:
      return "bg-slate-50 text-slate-600 border-slate-200";
  }
};

/**
 * Builds the "Subscription plan" line for a subscribed course. The member's
 * plan is the subscription-fee product itself, so the plan reads as the product
 * name with the recurring price appended when one is known.
 */
export const getSubscriptionPlanName = (product) => {
  const planName = product?.name || product?.product?.name || "";
  return planName || "Subscription Plan";
};

/** Recurring amount charged for a subscribed course, or null when unknown. */
export const getSubscriptionPlanAmount = (product) => {
  const amount = product?.price?.priceAmt ?? product?.product?.price?.priceAmt;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Billing cycle of a subscribed course, taken from its recurring price. */
export const getSubscriptionBillingCycle = (product) => getBillingCycleLabel(
  product?.price?.priceDurationType || product?.priceDurationType
);
